import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Plus, Search, GraduationCap, SortAsc, SortDesc, Filter, X, Users as UsersIcon, Share2, History } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { 
  getColleges, 
  createCollege, 
  updateCollegeStatus,
  searchCollegeList,
  College,
  CreateCollegeRequest,
  UpdateCollegeStatusRequest,
  CollegeFilters,
  CollegeListItem,
} from '../../services/collegeApi';
import { getAssignedManagers } from '../../services/apiclient';
import { useRbac } from '../../context';
import { cn } from '../../components/lib/utils';

// Define SPOC details interface
interface ISPOCDetails {
  name: string;
  email: string;
  phone: string;
  designation: string;
}

interface CollegeFormData {
  name: string;
  location: string;
  spocDetails: ISPOCDetails[];
  type?: string; // 'University' | 'College' | 'Institute'
}

interface FbdeUserOption {
  id: string;
  username: string;
  email?: string;
  role?: string;
}

interface SPOCErrors {
  name?: string;
  email?: string;
  phone?: string;
  designation?: string;
}

interface FormErrors {
  name?: string;
  location?: string;
  fbdeUserId?: string;
  spocDetails?: SPOCErrors[];
}

// SortableHeader component to match project pattern
const SortableHeader: React.FC<{
  column: string;
  className?: string;
  children: React.ReactNode;
  sortBy: string;
  sortOrder: "asc" | "desc";
  onSort: (field: string) => void;
}> = ({ column, className = "", children, sortBy, sortOrder, onSort }) => {
  return (
    <th
      className={`px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:bg-gray-50 sticky top-0 ${className}`}
      onClick={() => onSort(column)}
    >
      <div className="flex items-center gap-1">
        {children}
        {sortBy === column && (
          sortOrder === "asc" ? <SortAsc className="h-3 w-3" /> : <SortDesc className="h-3 w-3" />
        )}
      </div>
    </th>
  );
};

const CollegeReferralProgram: React.FC = () => {
  const [colleges, setColleges] = useState<College[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  // Separate state for main table filter type (not affected by modal form)
  const [listFilterType, setListFilterType] = useState<'College' | 'University' | 'Institute' | 'all'>('all');
  const [formData, setFormData] = useState<CollegeFormData>({
    name: '',
    location: '',
    spocDetails: [{ name: '', email: '', phone: '', designation: '' }],
    type: 'College'
  });
  
  // College search states
  const [collegeSearchTerm, setCollegeSearchTerm] = useState('');
  const [collegeSearchResults, setCollegeSearchResults] = useState<CollegeListItem[]>([]);
  const [isSearchingColleges, setIsSearchingColleges] = useState(false);
  const [showCollegeDropdown, setShowCollegeDropdown] = useState(false);
  const isSelectingRef = useRef(false);
  const [selectedFbdeUser, setSelectedFbdeUser] = useState<FbdeUserOption | null>(null);
  const [fbdeSearchTerm, setFbdeSearchTerm] = useState('');
  const [fbdeSearchResults, setFbdeSearchResults] = useState<FbdeUserOption[]>([]);
  const [isSearchingFbde, setIsSearchingFbde] = useState(false);
  const [showFbdeDropdown, setShowFbdeDropdown] = useState(false);
  const fbdeSelectingRef = useRef(false);
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [sortBy, setSortBy] = useState<string>('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  
  // Filter states
  const [selectedDateFrom, setSelectedDateFrom] = useState('');
  const [selectedDateTo, setSelectedDateTo] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalResults, setTotalResults] = useState(0);
  
  // College details popup state
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedCollege, setSelectedCollege] = useState<College | null>(null);
  
  // Status change modal state
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusChangeData, setStatusChangeData] = useState({
    collegeId: '',
    currentStatus: 'underProcess' as 'underProcess' | 'onboarded' | 'notOnboarded',
    newStatus: 'underProcess' as 'underProcess' | 'onboarded' | 'notOnboarded',
    remarks: ''
  });
  const [statusChangeErrors, setStatusChangeErrors] = useState<{remarks?: string}>({});
  const user = useRbac();

  // Load colleges on component mount
  useEffect(() => {
    fetchColleges();
  }, [listFilterType]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.college-search-container')) {
        setShowCollegeDropdown(false);
      }
    };

    if (showCollegeDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCollegeDropdown]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.fbde-search-container')) {
        setShowFbdeDropdown(false);
      }
    };

    if (showFbdeDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showFbdeDropdown]);

  const fetchColleges = async () => {
    try {
      setLoading(true);
      const filters: CollegeFilters = {
        page: currentPage,
        limit: itemsPerPage,
        search: searchTerm || undefined,
        status: selectedStatus !== 'all' ? selectedStatus as 'underProcess' | 'onboarded' | 'notOnboarded' : undefined,
        dateFrom: selectedDateFrom || undefined,
        dateTo: selectedDateTo || undefined,
        // use listFilterType for main table filtering, undefined for 'all'
        type: listFilterType !== 'all' ? listFilterType : undefined
      };
      
      const response = await getColleges(filters);
      setColleges(response.colleges);
      setTotalResults(response.totalResults);
    } catch (error) {
      console.error('Error fetching colleges:', error);
      toast.error('Failed to fetch colleges');
    } finally {
      setLoading(false);
    }
  };

  const fetchFbdeUsers = useCallback(async (searchValue: string) => {
    try {
      setIsSearchingFbde(true);
      const response = await getAssignedManagers('FBDE', searchValue);
      setFbdeSearchResults(Array.isArray(response) ? response : []);
    } catch (error) {
      console.error('Error fetching FBDE users:', error);
      setFbdeSearchResults([]);
    } finally {
      setIsSearchingFbde(false);
    }
  }, []);

  const validateForm = (): boolean => {
    const errors: FormErrors = {};
    
    if (!formData.name.trim()) {
      errors.name =
        formData.type === 'University'
          ? 'University name is required'
          : formData.type === 'Institute'
          ? 'Institute name is required'
          : 'College name is required';
    }
    
    if (!formData.location.trim()) {
      errors.location = 'Location is required';
    }

    // Validate SPOC details
    if (!formData.spocDetails || formData.spocDetails.length === 0) {
      errors.spocDetails = [];
    } else {
      const phoneRegex = /^[6-9]\d{9}$/;  // Indian mobile number validation
      const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;  // Email validation
      
      errors.spocDetails = formData.spocDetails.map(spoc => {
        const spocErrors: SPOCErrors = {};
        
        // Validate name
        if (!spoc.name.trim()) {
          spocErrors.name = 'Name is required';
        }
        
        // Validate phone
        if (!spoc.phone.trim()) {
          spocErrors.phone = 'Phone number is required';
        } else if (!phoneRegex.test(spoc.phone.trim())) {
          spocErrors.phone = 'Must be a valid 10-digit Indian mobile number starting with 6-9';
        }
        
        // Validate email (optional, but must be valid if provided)
        if (spoc.email.trim() && !emailRegex.test(spoc.email.trim())) {
          spocErrors.email = 'Must be a valid email address';
        }
        
        // Validate designation
        if (!spoc.designation.trim()) {
          spocErrors.designation = 'Designation is required';
        }
        
        return spocErrors;
      });
    }

    setFormErrors(errors);
    
    // Check if there are any errors
    const hasTopLevelErrors = Object.keys(errors).some(key => key !== 'spocDetails');
    const hasSpocErrors = errors.spocDetails && errors.spocDetails.length > 0 && 
                          errors.spocDetails.some(spoc => Object.keys(spoc).length > 0);
    
    return !hasTopLevelErrors && !hasSpocErrors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      setLoading(true);
     
      // Add new college
      const createData: CreateCollegeRequest = {
        name: formData.name,
        location: formData.location,
        type: formData.type,
        spocDetails: formData.spocDetails,
        college_type: formData.type
      };

      if (user.role === 'ADMIN') {
        createData.referredByFbdeUserId = selectedFbdeUser?.id ?? undefined;
      }
      
      await createCollege(createData);
      toast.success('College added successfully');
      setShowAddModal(false);
      
      resetForm();
      fetchColleges(); // Refresh the list
    } catch (error: any) {
      console.log('Error saving college:', error);
      if(error.status === 409) {
        toast.error('College with this name and location already exists');
      }else{
        toast.error('Failed to save college');
      }
      
    } finally {
      setLoading(false);
    }
  };


  const openStatusChangeModal = (collegeId: string, currentStatus: 'underProcess' | 'onboarded' | 'notOnboarded') => {
    setStatusChangeData({
      collegeId,
      currentStatus,
      newStatus: currentStatus,
      remarks: ''
    });
    setStatusChangeErrors({});
    setShowStatusModal(true);
  };

  const handleStatusChange = async () => {
    // Validate remarks requirement
    const errors: {remarks?: string} = {};
    if (statusChangeData.newStatus === 'notOnboarded' && !statusChangeData.remarks.trim()) {
      errors.remarks = 'Remarks are mandatory when status is set to "Not Onboarded"';
    }

    setStatusChangeErrors(errors);
    if (Object.keys(errors).length > 0) {      
      return;
    }

    try {
      setLoading(true);
      const statusData: UpdateCollegeStatusRequest = {
        status: statusChangeData.newStatus,
        remarks: statusChangeData.remarks.trim() || undefined
      };
      
      const updatedCollege = await updateCollegeStatus(statusChangeData.collegeId, statusData);
      
      // Update local state with the full updated college data including status history
      setColleges(prevColleges => 
        prevColleges.map(college => 
          college._id === statusChangeData.collegeId 
            ? updatedCollege
            : college
        )
      );
      
      // Update selectedCollege if it's the one that was updated
      if (selectedCollege && selectedCollege._id === statusChangeData.collegeId) {
        setSelectedCollege(updatedCollege);
      }
      
      toast.success(`College status updated to ${getStatusInfo(statusChangeData.newStatus).label}`);
      setShowStatusModal(false);
    } catch (error) {
      console.error('Error updating college status:', error);
      toast.error('Failed to update college status');
    } finally {
      setLoading(false);
    }
  };

  // College search function
  const searchColleges = useCallback(async (searchTerm: string, type: "College" | "University" | "Institute") => {
    console.log('Searching colleges/universities/institutes with term:', searchTerm);
    if (!searchTerm.trim()) {
      setCollegeSearchResults([]);
      setShowCollegeDropdown(false);
      return;
    }

    try {
      setIsSearchingColleges(true);
      console.log('Calling searchCollegeList API...', type);
      const results = await searchCollegeList(searchTerm, type);
      console.log('Search results:', results);
      setCollegeSearchResults(results);
      setShowCollegeDropdown(true);
    } catch (error) {
      console.error('Error searching colleges:', error);
      toast.error('Failed to search colleges');
      setCollegeSearchResults([]);
      setShowCollegeDropdown(false);
    } finally {
      setIsSearchingColleges(false);
    }
  }, []);

  useEffect(() => { 
    // Only trigger search if there's a search term when type changes
    if (collegeSearchTerm.trim()) {
      searchColleges(collegeSearchTerm, formData.type as "College" | "University" | "Institute");
    }
  }, [formData.type, collegeSearchTerm, searchColleges]);

  // Debounced search effect
  useEffect(() => {
    console.log('College search effect triggered for term:', collegeSearchTerm);
    
    // Don't trigger search if we're programmatically setting the search term
    if (isSelectingRef.current) {
      isSelectingRef.current = false;
      return;
    }
    
    if (!collegeSearchTerm.trim()) {
      setCollegeSearchResults([]);
      setShowCollegeDropdown(false);
      return;
    }

    const timeoutId = setTimeout(() => {
      console.log('Debounced timeout triggered, calling searchColleges');
      searchColleges(collegeSearchTerm, formData.type as "College" | "University" | "Institute");
    }, 300);

    return () => {
      console.log('Clearing timeout');
      clearTimeout(timeoutId);
    };
  }, [collegeSearchTerm, searchColleges, formData.type]);

  useEffect(() => {
    if (!showAddModal || user.role !== 'ADMIN') {
      return;
    }

    if (fbdeSelectingRef.current) {
      fbdeSelectingRef.current = false;
      return;
    }

    const debounceId = setTimeout(() => {
      fetchFbdeUsers(fbdeSearchTerm.trim());
    }, 300);

    return () => {
      clearTimeout(debounceId);
    };
  }, [showAddModal, user.role, fbdeSearchTerm, fetchFbdeUsers]);

  // Handle college selection
  const handleCollegeSelect = (college: CollegeListItem) => {
    console.log('College selected:', college);
    
    // Always close dropdown immediately and clear results
    setShowCollegeDropdown(false);
    setCollegeSearchResults([]);
    
    if(college?.status === "underProcess") {
      toast.error(formData.type === 'College' ? "College is already under process" : formData.type === 'University' ? "University is already under process" : "Institute is already under process");
      return;
    }
    if(college?.status === "onboarded") {
      toast.error(formData.type === 'College' ? "College is already onboarded" : formData.type === 'University' ? "University is already onboarded" : "Institute is already onboarded");
      return;
    }
    if(college?.status === "notOnboarded") {
      toast.error(formData.type === 'College' ? "College is not onboarded, please select a different college" : formData.type === 'University' ? "University is not onboarded, please select a different university" : "Institute is not onboarded, please select a different institute");
      return;
    }
    
    // Set flag to prevent search trigger BEFORE updating state
    isSelectingRef.current = true;
    
    setFormData({
      ...formData,
      name: formData.type === 'College' ? college.college : formData.type === 'University' ? college.university : college.college,
      location: formData.type === 'College' || formData.type === 'Institute' ? `${college.district}, ${college.state}` : ``
      
    });
    
    // Clear the search term to prevent dropdown from reappearing
    setCollegeSearchTerm('');
    
    toast.success(`Selected: ${formData.type === 'College' ? college.college : college.university}`);
  };

  const handleFbdeSelect = (fbde: FbdeUserOption) => {
    fbdeSelectingRef.current = true;
    setSelectedFbdeUser(fbde);
    setFbdeSearchTerm('');
    setShowFbdeDropdown(false);
  };

  const handleClearSelectedFbde = () => {
    setSelectedFbdeUser(null);
    setShowFbdeDropdown(false);
    fbdeSelectingRef.current = false;
  };

  const resetForm = () => {
    setFormData({
      name: '',
      location: '',
      spocDetails: [{ name: '', email: '', phone: '', designation: '' }],
      type: 'College'
    });
    setFormErrors({});
    // Reset college search states
    setCollegeSearchTerm('');
    setCollegeSearchResults([]);
    setShowCollegeDropdown(false);
    setSelectedFbdeUser(null);
    setFbdeSearchTerm('');
    setFbdeSearchResults([]);
    setShowFbdeDropdown(false);
    fbdeSelectingRef.current = false;
  };

  const handleTypeChange = (newType: string) => {
    setFormData({
      name: '',
      location: '',
      spocDetails: [{ name: '', email: '', phone: '', designation: '' }],
      type: newType
    });
    setFormErrors({});
    // Reset college search states
    setCollegeSearchTerm('');
    setCollegeSearchResults([]);
    setShowCollegeDropdown(false);
  };


  // SPOC handling functions
  const addSpoc = () => {
    setFormData(prev => ({
      ...prev,
      spocDetails: [...prev.spocDetails, { name: '', email: '', phone: '', designation: '' }],
    }));
  };

  const removeSpoc = (index: number) => {
    setFormData(prev => ({
      ...prev,
      spocDetails: prev.spocDetails.filter((_, i) => i !== index),
    }));
  };

  const handleSpocChange = (e: React.ChangeEvent<HTMLInputElement>, spocIndex: number) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      spocDetails: prev.spocDetails.map((spoc, index) =>
        index === spocIndex ? { ...spoc, [name]: value } : spoc
      ),
    }));
  };

  // Load colleges when search term or filters change
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchColleges();
    }, 500); // Debounce search

    return () => clearTimeout(timeoutId);
  }, [searchTerm, selectedStatus, selectedDateFrom, selectedDateTo, currentPage, itemsPerPage]);

  // Reset pagination to page 1 when filters change
  useEffect(() => {
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  }, [searchTerm, selectedStatus, selectedDateFrom, selectedDateTo]);

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  // Pagination functions
  const totalPages = Math.ceil(totalResults / itemsPerPage);
  
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  // College details popup handler
  const handleCollegeClick = (college: College) => {
    setSelectedCollege(college);
    setShowDetailsModal(true);
  };

  const sortedColleges = [...colleges].sort((a, b) => {
    let aValue: any = a[sortBy as keyof College];
    let bValue: any = b[sortBy as keyof College];
    
    if (typeof aValue === 'string') {
      aValue = aValue.toLowerCase();
      bValue = bValue.toLowerCase();
    }
    
    if (sortOrder === 'asc') {
      return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
    } else {
      return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
    }
  });

  // Clear all filters
  const clearAllFilters = () => {
    setSearchTerm('');
    setSelectedDateFrom('');
    setSelectedDateTo('');
    setSelectedStatus('all');
    setListFilterType('all'); // Reset to default: show all types
  };

  // Get active filters
  const getActiveFilters = () => {
    const filters = [];
    if (searchTerm) filters.push({ key: 'search', value: searchTerm, label: 'Search' });
    if (selectedDateFrom) filters.push({ key: 'dateFrom', value: selectedDateFrom, label: 'From Date' });
    if (selectedDateTo) filters.push({ key: 'dateTo', value: selectedDateTo, label: 'To Date' });
    if (selectedStatus !== 'all') filters.push({ key: 'status', value: selectedStatus, label: 'Status' });
    return filters;
  };

  const activeFilters = getActiveFilters();

  // Helper function to get status display info
  const getStatusInfo = (status: string) => {
    switch (status) {
      case 'underProcess':
        return { label: 'Under Process', variant: 'secondary' as const, className: 'bg-yellow-100 text-yellow-800' };
      case 'onboarded':
        return { label: 'Onboarded', variant: 'default' as const, className: 'bg-green-100 text-green-800' };
      case 'notOnboarded':
        return { label: 'Not Onboarded', variant: 'destructive' as const, className: 'bg-red-100 text-red-800' };
      case 'added':
        return { label: 'Added', variant: 'default' as const, className: 'bg-green-100 text-green-800' };
      case 'approched':
        return { label: 'Approched', variant: 'default' as const, className: 'bg-green-100 text-green-800' };
      case 'inTalk':
        return { label: 'In Talk', variant: 'default' as const, className: 'bg-green-100 text-green-800' };
      case 'confirmed':
        return { label: 'Confirmed', variant: 'default' as const, className: 'bg-green-100 text-green-800' };
      case 'activated':
        return { label: 'Activated', variant: 'default' as const, className: 'bg-green-100 text-green-800' };
      case 'onHold':
        return { label: 'On Hold', variant: 'default' as const, className: 'bg-gray-100 text-gray-800' };
      default:
        return { label: status, variant: 'secondary' as const, className: 'bg-gray-100 text-gray-800' };
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      {/* <Card className="shadow-lg flex flex-col flex-1 min-h-0"> */}
        <CardHeader className="flex-shrink-0">
          <div className="flex flex-col space-y-4 sm:space-y-0 sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center space-x-2 sm:space-x-4">
              <GraduationCap className="h-6 w-6 text-orange-600" />
              <div className="min-w-0 flex-1">
                <h1 className="text-lg sm:text-xl font-semibold text-gray-900 truncate">
                  College Referral Program
                </h1>
                <p className="text-xs sm:text-sm text-gray-500 mb-3">
                  Manage college partnerships and referrals
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={() => {
                  resetForm();
                  setShowAddModal(true);
                }}
                className="bg-orange-600 hover:bg-orange-700"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add College / University
              </Button>
            </div>
          </div>

          <div className="mt-3">
            {/* Mobile: Vertical column layout for filters */}
            <div className="block md:hidden space-y-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  placeholder="Search colleges..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 w-full h-10 rounded-lg border-gray-200 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                />
              </div>
              <div>
                <Select
                  value={selectedStatus}
                  onValueChange={(value) => setSelectedStatus(value)}
                >
                  <SelectTrigger className="w-full h-10 rounded-lg border-gray-200 focus:ring-2 focus:ring-orange-500 focus:border-orange-500">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="underProcess">Under Process</SelectItem>
                    <SelectItem value="onboarded">Onboarded</SelectItem>
                    <SelectItem value="notOnboarded">Not Onboarded</SelectItem>
                    <SelectItem value="added">Added</SelectItem>
                    <SelectItem value="approched">Approched</SelectItem>
                    <SelectItem value="inTalk">In Talk</SelectItem>
                    <SelectItem value="confirmed">Confirmed</SelectItem>
                    <SelectItem value="activated">Activated</SelectItem>
                    <SelectItem value="onHold">On Hold</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-3">
                <Input
                  type="date"
                  value={selectedDateFrom}
                  onChange={(e) => setSelectedDateFrom(e.target.value)}
                  className="w-full h-10 rounded-lg border-gray-200 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  title="From Date"
                />
                <Input
                  type="date"
                  value={selectedDateTo}
                  onChange={(e) => setSelectedDateTo(e.target.value)}
                  className="w-full h-10 rounded-lg border-gray-200 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  title="To Date"
                />
              </div>
              <div>
                <Select
                  value={listFilterType}
                  onValueChange={(value) => setListFilterType(value as 'College' | 'University' | 'Institute' | 'all')}
                >
                  <SelectTrigger className="w-full h-10 rounded-lg border-gray-200 focus:ring-2 focus:ring-orange-500 focus:border-orange-500">
                    <SelectValue placeholder="Type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="College">College</SelectItem>
                    <SelectItem value="University">University</SelectItem>
                    <SelectItem value="Institute">Institute</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Desktop: Regular flex layout */}
            <div className="hidden md:flex flex-wrap gap-2">
              <div className="relative w-64">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  placeholder="Search colleges..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 w-full h-10 rounded-lg border-gray-200 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                />
              </div>
              <Select
                value={listFilterType}
                onValueChange={(value) => setListFilterType(value as 'College' | 'University' | 'all')}
              >
                <SelectTrigger className="w-40 h-10 rounded-lg border-gray-200 focus:ring-2 focus:ring-orange-500 focus:border-orange-500">
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="College">College</SelectItem>
                  <SelectItem value="University">University</SelectItem>
                  <SelectItem value="Institute">Institute</SelectItem>
                </SelectContent>
              </Select>
              <Select
                value={selectedStatus}
                onValueChange={(value) => setSelectedStatus(value)}
              >
                <SelectTrigger className="w-48 h-10 rounded-lg border-gray-200 focus:ring-2 focus:ring-orange-500 focus:border-orange-500">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="underProcess">Under Process</SelectItem>
                    <SelectItem value="onboarded">Onboarded</SelectItem>
                    <SelectItem value="notOnboarded">Not Onboarded</SelectItem>
                    <SelectItem value="added">Added</SelectItem>
                    <SelectItem value="approched">Approched</SelectItem>
                    <SelectItem value="inTalk">In Talk</SelectItem>
                    <SelectItem value="confirmed">Confirmed</SelectItem>
                    <SelectItem value="activated">Activated</SelectItem>
                    <SelectItem value="onHold">On Hold</SelectItem>
                </SelectContent>
              </Select>
              <Input
                type="date"
                value={selectedDateFrom}
                onChange={(e) => setSelectedDateFrom(e.target.value)}
                className="w-40 h-10 rounded-lg border-gray-200 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                title="From Date"
              />
              <Input
                type="date"
                value={selectedDateTo}
                onChange={(e) => setSelectedDateTo(e.target.value)}
                className="w-40 h-10 rounded-lg border-gray-200 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                title="To Date"
              />
            </div>
          </div>

          {activeFilters.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap mt-3">
              <span className="text-sm text-muted-foreground">Active filters:</span>
              {activeFilters.map((filter) => (
                <Badge key={filter.key} variant="secondary" className="flex items-center gap-1">
                  {filter.label}: {filter.value}
                  <X
                    className="w-3 h-3 cursor-pointer"
                    onClick={() => {
                      if (filter.key === 'search') setSearchTerm('');
                      if (filter.key === 'dateFrom') setSelectedDateFrom('');
                      if (filter.key === 'dateTo') setSelectedDateTo('');
                      if (filter.key === 'status') setSelectedStatus('all');
                    }}
                  />
                </Badge>
              ))}
              <Button variant="ghost" size="sm" onClick={clearAllFilters} className="text-xs">
                Clear all
              </Button>
            </div>
          )}
        </CardHeader>
        <CardContent className="flex flex-col flex-1 min-h-0">
          <div className="bg-background relative flex-1 flex flex-col min-h-0">
            <div className="flex-1 overflow-auto">
              {/* Mobile Card View */}
              <div className="block md:hidden space-y-4">
                {loading ? (
                  // Mobile shimmer loading
                  Array.from({ length: 5 }).map((_, index) => (
                    <div key={`mobile-shimmer-${index}`} className="animate-pulse bg-white rounded-lg border border-gray-200 p-4">
                      <div className="space-y-3">
                        <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                        <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                        <div className="h-3 bg-gray-200 rounded w-1/3"></div>
                        <div className="h-6 bg-gray-200 rounded w-20"></div>
                      </div>
                    </div>
                  ))
                ) : sortedColleges.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    {searchTerm ? 'No colleges found matching your search.' : 'No colleges added yet.'}
                  </div>
                ) : (
                  sortedColleges.map((college) => (
                    <div key={college._id} className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
                      <div className="space-y-3">
                        <div className="flex items-start justify-between">
                          <button
                            onClick={() => handleCollegeClick(college)}
                            className="text-left font-medium text-gray-900 hover:text-gray-700 cursor-pointer text-base leading-tight"
                          >
                            {college.name}
                          </button>
                          <Badge 
                            variant={getStatusInfo(college.status).variant}
                            className={`${getStatusInfo(college.status).className} text-xs`}
                          >
                            {getStatusInfo(college.status).label}
                          </Badge>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div>
                            <span className="text-gray-500 font-medium">Location:</span>
                            <p className="text-gray-900">{college.location}</p>
                          </div>
                          <div>
                            <span className="text-gray-500 font-medium">Date:</span>
                            <p className="text-gray-900">{new Date(college.createdAt).toLocaleDateString()}</p>
                          </div>
                          <div>
                            <span className="text-gray-500 font-medium">Referred By:</span>
                            <p className="text-gray-900">{college.referredBy?.username || 'N/A'}</p>
                          </div>
                          <div>
                            <span className="text-gray-500 font-medium">Led By:</span>
                            <p className="text-gray-900">
                              {typeof college.referredBy?.ledby === 'object' 
                                ? college.referredBy.ledby.username || 'N/A'
                                : college.referredBy?.ledby || 'N/A'}
                            </p>
                          </div>
                          {(user.role === "ADMIN" || user.role === "MASTER_BDE") && (
                            <div className="col-span-2">
                              <span className="text-gray-500 font-medium">Actions:</span>
                              <div className="mt-1">
                                <Select
                                  value={college.status}
                                  onValueChange={(value) => openStatusChangeModal(college._id, value as 'underProcess' | 'onboarded' | 'notOnboarded')}
                                >
                                  <SelectTrigger className="w-full h-8 text-xs">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="underProcess">Under Process</SelectItem>
                                    <SelectItem value="onboarded">Onboarded</SelectItem>
                                    <SelectItem value="notOnboarded">Not Onboarded</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Desktop Table View */}
              <div className="hidden md:block overflow-x-auto">
                <table className="min-w-full divide-y divide-border">
                  <thead style={{ backgroundColor: '#FFF5F2' }}>
                    <tr>
                      <SortableHeader 
                        column="name" 
                        className="min-w-48"
                        sortBy={sortBy}
                        sortOrder={sortOrder}
                        onSort={handleSort}
                      >
                        College / University / Institute Name
                      </SortableHeader>
                      <SortableHeader 
                        column="location" 
                        className="min-w-32"
                        sortBy={sortBy}
                        sortOrder={sortOrder}
                        onSort={handleSort}
                      >
                        Location
                      </SortableHeader>
                      <SortableHeader 
                        column="createdAt" 
                        className="min-w-32"
                        sortBy={sortBy}
                        sortOrder={sortOrder}
                        onSort={handleSort}
                      >
                        createdAT
                      </SortableHeader>
                      <SortableHeader 
                        column="createdAt" 
                        className="min-w-32"
                        sortBy={sortBy}
                        sortOrder={sortOrder}
                        onSort={handleSort}
                      >
                        referredBy
                      </SortableHeader>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider min-w-32">
                        Led By
                      </th>
                      <SortableHeader 
                        column="type" 
                        className="min-w-32"
                        sortBy={sortBy}
                        sortOrder={sortOrder}
                        onSort={handleSort}
                      >
                        Type
                      </SortableHeader>
                      <SortableHeader 
                        column="status" 
                        className="min-w-32"
                        sortBy={sortBy}
                        sortOrder={sortOrder}
                        onSort={handleSort}
                      >
                        Status
                      </SortableHeader>
                      {(user.role === "ADMIN" || user.role === "MASTER_BDE") && (
                        <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider min-w-32">
                          Actions
                        </th>
                      )}
                    
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      // Shimmer loading rows
                      Array.from({ length:11 }).map((_, index) => (
                        <tr key={`shimmer-${index}`} className="animate-pulse h-16">
                          <td className="px-4 py-3">
                            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="h-4 bg-gray-200 rounded w-1/3"></div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="h-4 bg-gray-200 rounded w-1/4"></div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="h-6 bg-gray-200 rounded w-20"></div>
                          </td>
                          {user.role === "ADMIN" && (
                            <td className="px-4 py-3">
                              <div className="h-6 bg-gray-200 rounded w-24"></div>
                            </td>
                          )}
                        </tr>
                      ))
                    ) : sortedColleges.length === 0 ? (
                      <tr className="h-16">
                        <td colSpan={user.role === "ADMIN" ? 7 : 6} className="px-4 py-12 text-center text-gray-500">
                          {searchTerm ? 'No colleges/universities found matching your search.' : 'No colleges/universities added yet.'}
                        </td>
                      </tr>
                    ) : (
                      sortedColleges.map((college) => (
                        <tr key={college._id} className="hover:bg-gray-5 border-b border-gray-200 h-16">
                          <td className="px-4 py-3 text-sm font-medium text-gray-900">
                            <button
                              onClick={() => handleCollegeClick(college)}
                              className="text-gray-900 hover:text-gray-700 cursor-pointer text-left font-medium"
                            >
                              {college.name}
                            </button>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700">{college.location}</td>
                          <td className="px-4 py-3 text-sm text-gray-700">{new Date(college.createdAt).toLocaleDateString()}</td>
                          <td className="px-4 py-3 text-sm text-gray-700">{college.referredBy?.username  || 'N/A'}</td>
                          <td className="px-4 py-3 text-sm text-gray-700">
                            {typeof college.referredBy?.ledby === 'object' 
                              ? college.referredBy.ledby.username || 'N/A'
                              : college.referredBy?.ledby || 'N/A'}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700">{college.type  || 'N/A'}</td>
                          <td className="px-4 py-3 text-sm">
                            <Badge 
                              variant={getStatusInfo(college.status).variant}
                              className={getStatusInfo(college.status).className}
                            >
                              {getStatusInfo(college.status).label}
                            </Badge>
                          </td>
                          {(user.role === "ADMIN" || user.role === "MASTER_BDE") && (
                            <td className="px-4 py-3 text-sm">
                              <Select
                                value={college.status}
                                onValueChange={(value) => openStatusChangeModal(college._id, value as 'underProcess' | 'onboarded' | 'notOnboarded')}
                              >
                                <SelectTrigger className="w-40 h-8 text-xs text-left">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                <SelectItem value="added">Added</SelectItem>
                                <SelectItem value="approched">Approched</SelectItem>
                                <SelectItem value="inTalk">In Talk</SelectItem>
                                <SelectItem value="confirmed">Confirmed</SelectItem>
                                <SelectItem value="activated">Activated</SelectItem>
                                <SelectItem value="onHold">On Hold</SelectItem>
                                  <SelectItem value="underProcess">Under Process</SelectItem>
                                  <SelectItem value="onboarded">Onboarded</SelectItem>
                                  <SelectItem value="notOnboarded">Not Onboarded</SelectItem>
                                </SelectContent>
                              </Select>
                            </td>
                          )}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            
            {/* Pagination Controls */}
            <div className="flex flex-col sm:flex-row items-center justify-between px-4 py-3 border-t border-gray-200 bg-white gap-3 flex-shrink-0">
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-700">Show</span>
                <Select
                  value={itemsPerPage.toString()}
                  onValueChange={(value) => handleItemsPerPageChange(parseInt(value))}
                >
                  <SelectTrigger className="w-16 sm:w-20 h-8">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="5">5</SelectItem>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="20">20</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                  </SelectContent>
                </Select>
                <span className="text-sm text-gray-700">per page</span>
              </div>
              
              {totalResults > 0 && (
                <div className="flex items-center space-x-2">
                  <span className="text-xs sm:text-sm text-gray-700">
                    Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, totalResults)} of {totalResults} results
                  </span>
                </div>
              )}
              
              {totalPages > 1 && (
                <div className="flex items-center space-x-1">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="h-8 w-8 p-0"
                  >
                    ←
                  </Button>
                  
                  {/* Mobile: Show fewer page numbers */}
                  <div className="hidden sm:flex items-center space-x-1">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      const pageNum = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
                      if (pageNum > totalPages) return null;
                      
                      return (
                        <Button
                          key={pageNum}
                          variant={currentPage === pageNum ? "default" : "outline"}
                          size="sm"
                          onClick={() => handlePageChange(pageNum)}
                          className={`h-8 w-8 p-0 ${currentPage === pageNum ? 'bg-orange-600 hover:bg-orange-700' : ''}`}
                        >
                          {pageNum}
                        </Button>
                      );
                    })}
                  </div>
                  
                  {/* Mobile: Show current page and ellipsis */}
                  <div className="flex sm:hidden items-center space-x-1">
                    {/* Show first page */}
                    {currentPage > 3 && (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handlePageChange(1)}
                          className="h-8 w-8 p-0"
                        >
                          1
                        </Button>
                        <span className="text-gray-500 px-1">...</span>
                      </>
                    )}
                    
                    {/* Show previous page if not first page */}
                    {currentPage > 1 && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(currentPage - 1)}
                        className="h-8 w-8 p-0"
                      >
                        {currentPage - 1}
                      </Button>
                    )}
                    
                    {/* Show current page */}
                    <Button
                      variant="default"
                      size="sm"
                      className="h-8 w-8 p-0 bg-orange-600 hover:bg-orange-700"
                    >
                      {currentPage}
                    </Button>
                    
                    {/* Show next page if not last page */}
                    {currentPage < totalPages && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(currentPage + 1)}
                        className="h-8 w-8 p-0"
                      >
                        {currentPage + 1}
                      </Button>
                    )}
                    
                    {/* Show last page with ellipsis */}
                    {currentPage < totalPages - 2 && (
                      <>
                        <span className="text-gray-500 px-1">...</span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handlePageChange(totalPages)}
                          className="h-8 w-8 p-0"
                        >
                          {totalPages}
                        </Button>
                      </>
                    )}
                  </div>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="h-8 w-8 p-0"
                  >
                    →
                  </Button>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      {/* </Card> */}

      {/* Add College Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="w-[95vw] max-w-4xl mx-auto max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold">Add New College</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-6">

            {user.role === 'ADMIN' && (
              <div className="space-y-2">
                <Label className="text-sm font-medium">Assign FBDE (optional)</Label>
                <div className="fbde-search-container relative">
                  <Input
                    value={fbdeSearchTerm}
                    onChange={(e) => {
                      setFbdeSearchTerm(e.target.value);
                      setShowFbdeDropdown(true);
                    }}
                    placeholder="Search FBDE by name, email, or user ID"
                    className="w-full"
                    onFocus={() => {
                      setShowFbdeDropdown(true);
                      if (!fbdeSearchResults.length && !isSearchingFbde) {
                        fetchFbdeUsers(fbdeSearchTerm.trim());
                      }
                    }}
                    autoComplete="off"
                  />
                  {isSearchingFbde && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-orange-600" />
                    </div>
                  )}
                  {showFbdeDropdown && (
                    <div className="absolute z-50 mt-1 max-h-60 w-full overflow-y-auto rounded-md border border-gray-200 bg-white shadow-lg">
                      {isSearchingFbde ? (
                        <div className="flex items-center justify-center gap-2 py-4 text-sm text-gray-500">
                          <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-orange-600" />
                          <span>Searching FBDE users...</span>
                        </div>
                      ) : fbdeSearchResults.length > 0 ? (
                        fbdeSearchResults.map((fbde) => {
                          const isSelected = selectedFbdeUser?.id === fbde.id;
                          return (
                            <div
                              key={fbde.id}
                              className={cn(
                                'cursor-pointer border-b border-gray-100 px-4 py-3 last:border-b-0',
                                isSelected
                                  ? 'bg-orange-50 text-orange-600'
                                  : 'hover:bg-gray-50 text-gray-700'
                              )}
                              onClick={() => handleFbdeSelect(fbde)}
                            >
                              <div className="text-sm font-medium">{fbde.username}</div>
                              {fbde.email && (
                                <div className="text-xs text-gray-500">{fbde.email}</div>
                              )}
                              <div className="text-xs text-gray-400">{fbde.role || 'FBDE'}</div>
                            </div>
                          );
                        })
                      ) : (
                        <div className="px-4 py-3 text-sm text-gray-500">
                          No FBDE users found. Try a different search.
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {selectedFbdeUser && (
                  <div className="flex items-center justify-between rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
                    <div>
                      <p className="text-sm font-medium text-gray-900">{selectedFbdeUser.username}</p>
                      {selectedFbdeUser.email && (
                        <p className="text-xs text-gray-500">{selectedFbdeUser.email}</p>
                      )}
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={handleClearSelectedFbde}
                      className="text-xs text-orange-600 hover:text-orange-700"
                    >
                      Clear
                    </Button>
                  </div>
                )}
              </div>
            )}

             <div className="col-span-1 lg:col-span-2">
                <Label className="text-sm font-medium">Type</Label>
                <div className="flex items-center gap-4 mt-2">
                  <label className="inline-flex items-center gap-2">
                    <input
                      type="radio"
                      name="type"
                      value="College"   
                      checked={formData.type === 'College'}
                      onChange={() => handleTypeChange('College')}
                      className="h-4 w-4 text-orange-600 accent-orange-600 cursor-pointer"
                    />
                    <span className="text-sm">College</span>
                  </label>
                  <label className="inline-flex items-center gap-2">
                    <input
                      type="radio"
                      name="type"
                      value="University"
                      checked={formData.type === 'University'}
                      onChange={() => handleTypeChange('University')}
                      className="h-4 w-4 text-orange-600 accent-orange-600 cursor-pointer"
                    />
                    <span className="text-sm">University</span>
                  </label>

                   <label className="inline-flex items-center gap-2">
                    <input
                      type="radio"
                      name="type"
                      value="Institute"
                      checked={formData.type === 'Institute'}
                      onChange={() => handleTypeChange('Institute')}
                      className="h-4 w-4 text-orange-600 accent-orange-600 cursor-pointer"
                    />
                    <span className="text-sm">Institute</span>
                  </label>
                </div>
              </div>
            {/* College Search Section */}
             <div className="bg-grey-50 rounded-lg">
              <h3 className="text-sm font-medium text-gray-700 mb-3">{formData.type === 'College' ? 'Search College' :formData.type === 'University' ? 'Search University' : 'Search Institute'}</h3>
              <div className="relative college-search-container">
                <Input
                  value={collegeSearchTerm}
                  onChange={(e) => {
                    console.log('Input changed:', e.target.value);
                    setCollegeSearchTerm(e.target.value);
                  }}
                  className="w-full"
                  placeholder={`Search by ${formData.type === 'University' ? 'university' :formData.type === 'College' ? 'college' : 'institute'} name, location...`}
                  onFocus={() => {
                    if (collegeSearchResults.length > 0 && !isSelectingRef.current) {
                      setShowCollegeDropdown(true);
                    }
                  }}
                />
                {isSearchingColleges && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-orange-600"></div>
                  </div>
                )}
                
                 {/* College Dropdown */}
                 {showCollegeDropdown && collegeSearchResults.length > 0 && (
                   <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-y-auto">
                     {collegeSearchResults.map((college, index) => (
                       <div
                         key={index}
                         className={cn("px-4 py-3 cursor-pointer border-b border-gray-100 last:border-b-0",  ["underProcess","onboarded","notOnboarded"].includes(college?.status || "") ? "bg-red-50 hover:bg-orange-50" : "bg-white-50 hover:bg-gray-50")}
                         onClick={() => handleCollegeSelect(college)}
                       >{formData.type === 'College' || formData.type === 'Institute' ?  <>
                          <div className="font-sm text-gray-900">{college.college}</div>
                           <div className="text-sm text-gray-500">
                           {college.district}, {college.state}
                         </div></>: <div className="font-sm text-gray-900">{college.university}</div>}
                        
                       </div>
                     ))} 
                   </div>
                 )}
                 
                 {/* No Results Warning */}
                 {showCollegeDropdown && collegeSearchResults.length === 0 && collegeSearchTerm.trim() && !isSearchingColleges && (
                   <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg">
                     <div className="px-4 py-3 text-center text-gray-500">
                       <div className="text-sm font-medium text-gray-700">{formData.type === 'College' ? 'No colleges found' : 'No universities found'}</div>
                       <div className="text-xs text-gray-500 mt-1">
                         Try searching with different keywords or fill the form manually
                       </div>
                     </div>
                   </div>
                 )}
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Select a {formData.type === 'University' ? 'university' :formData.type === 'College' ? 'college' : 'institute'} from the search results to auto-fill the form below, or fill manually.
              </p>
            </div>

            {/* Basic Information - Two Column Grid for Desktop */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
             
              <div className="space-y-2">
                <Label htmlFor="name" className="text-sm font-medium">
                 {formData.type === 'University' ? 'University' : formData.type === 'College' ? 'College' : 'Institute'} Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className={`w-full ${formErrors.name ? "border-red-500 focus:border-red-500" : ""}`}
                  placeholder={formData.type === 'University' ? "Enter university name" : formData.type === 'College' ? "Enter college name" : "Enter institute name"}
                />
                {formErrors.name && <p className="text-sm text-red-500">{formErrors.name}</p>}
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="location" className="text-sm font-medium">
                  Location <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="location"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  className={`w-full ${formErrors.location ? "border-red-500 focus:border-red-500" : ""}`}
                  placeholder="Enter location"
                />
                {formErrors.location && <p className="text-sm text-red-500">{formErrors.location}</p>}
              </div>
              
              {/* <div className="space-y-2">
                <Label htmlFor="contactPerson" className="text-sm font-medium">
                  Contact Person <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="contactPerson"
                  value={formData.contactPerson}
                  onChange={(e) => setFormData({ ...formData, contactPerson: e.target.value })}
                  className={`w-full ${formErrors.contactPerson ? "border-red-500 focus:border-red-500" : ""}`}
                  placeholder="Enter contact person name"
                />
                {formErrors.contactPerson && <p className="text-sm text-red-500">{formErrors.contactPerson}</p>}
              </div> */}
            </div>
            
            {/* SPOC Details Section */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-medium">
                  SPOC Details <span className="text-red-500">*</span> (at least one required)
                </Label> 
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addSpoc}
                  className="border-orange-300 text-orange-600 hover:bg-orange-50 transition-colors"
                >
                  <Plus className="h-4 w-4 mr-2" /> Add SPOC
                </Button>
              </div>
              {formData.spocDetails.map((spoc, index) => (
                <div
                  key={index}
                  className="border border-gray-200 p-4 rounded-md bg-gray-50 relative"
                >
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute top-2 right-2 text-gray-500 hover:text-red-600 transition-colors"
                    onClick={() => removeSpoc(index)}
                    disabled={formData.spocDetails.length === 1}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                  <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor={`spocName-${index}`} className="text-sm font-medium">
                        SPOC Name <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id={`spocName-${index}`}
                        name="name"
                        value={spoc.name}
                        onChange={(e) => handleSpocChange(e, index)}
                        className={cn(
                          "w-full border-gray-200 focus:ring-2 focus:ring-orange-500 focus:border-orange-500",
                          formErrors.spocDetails?.[index]?.name && "border-red-500"
                        )}
                        placeholder="Enter SPOC name"
                      />
                      {formErrors.spocDetails?.[index]?.name && (
                        <p className="text-sm text-red-500 mt-1">{formErrors.spocDetails[index].name}</p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor={`spocPhone-${index}`} className="text-sm font-medium">
                        SPOC Phone <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id={`spocPhone-${index}`}
                        name="phone"
                        type="tel"
                        value={spoc.phone}
                        maxLength={10}
                        onChange={(e) => handleSpocChange(e, index)}
                        className={cn(
                          "w-full border-gray-200 focus:ring-2 focus:ring-orange-500 focus:border-orange-500",
                          formErrors.spocDetails?.[index]?.phone && "border-red-500"
                        )}
                        placeholder="Enter SPOC phone"
                      />
                      {formErrors.spocDetails?.[index]?.phone && (
                        <p className="text-sm text-red-500 mt-1">{formErrors.spocDetails[index].phone}</p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor={`spocEmail-${index}`} className="text-sm font-medium">
                        SPOC Email
                      </Label>
                      <Input
                        id={`spocEmail-${index}`}
                        name="email"
                        type="email"
                        value={spoc.email}
                        onChange={(e) => handleSpocChange(e, index)}
                        className={cn(
                          "w-full border-gray-200 focus:ring-2 focus:ring-orange-500 focus:border-orange-500",
                          formErrors.spocDetails?.[index]?.email && "border-red-500"
                        )}
                        placeholder="Enter SPOC email (optional)"
                      />
                      {formErrors.spocDetails?.[index]?.email && (
                        <p className="text-sm text-red-500 mt-1">{formErrors.spocDetails[index].email}</p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor={`spocdesignation-${index}`} className="text-sm font-medium">
                        SPOC Designation <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id={`spocdesignation-${index}`}
                        name="designation"
                        value={spoc.designation}
                        onChange={(e) => handleSpocChange(e, index)}
                        className={cn(
                          "w-full border-gray-200 focus:ring-2 focus:ring-orange-500 focus:border-orange-500",
                          formErrors.spocDetails?.[index]?.designation && "border-red-500"
                        )}
                        placeholder="Enter SPOC designation"
                      />
                      {formErrors.spocDetails?.[index]?.designation && (
                        <p className="text-sm text-red-500 mt-1">{formErrors.spocDetails[index].designation}</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            <DialogFooter className="flex flex-col sm:flex-row gap-2 pt-4">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setShowAddModal(false)}
                className="w-full sm:w-auto order-2 sm:order-1"
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={loading} 
                className="w-full sm:w-auto bg-orange-600 hover:bg-orange-700 order-1 sm:order-2"
              >
                {loading ? 'Adding...' : `Add ${formData.type === 'University' ? 'University' : formData.type === 'College' ? 'College' : 'Institute'}`}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>


      {/* College Details Modal */}
      <Dialog open={showDetailsModal} onOpenChange={setShowDetailsModal}>
        <DialogContent className="w-[95vw] max-w-4xl mx-auto max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold flex items-center gap-2">
              <GraduationCap className="h-5 w-5 text-orange-600" />
              College Details
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto pr-1">
            {selectedCollege && (
              <div className="space-y-6">
                {/* Basic Information */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div>
                      <Label className="text-sm font-medium text-gray-500">College Name</Label>
                      <p className="text-lg font-semibold text-gray-900">{selectedCollege.name}</p>
                    </div>
                    
                    <div>
                      <Label className="text-sm font-medium text-gray-500">Location</Label>
                      <p className="text-gray-900">{selectedCollege.location}</p>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <Label className="text-sm font-medium text-gray-500">Status</Label>
                      <div className="mt-1">
                        <Badge
                          variant={getStatusInfo(selectedCollege.status).variant}
                          className={getStatusInfo(selectedCollege.status).className}
                        >
                          {getStatusInfo(selectedCollege.status).label}
                        </Badge>
                      </div>
                    </div>
                    
                    <div>
                      <Label className="text-sm font-medium text-gray-500">Created At</Label>
                      <p className="text-gray-900">
                        {new Date(selectedCollege.createdAt).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                    
                    <div>
                      <Label className="text-sm font-medium text-gray-500">Last Updated</Label>
                      <p className="text-gray-900">
                        {new Date(selectedCollege.updatedAt).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Referral Details */}
                <div className="border-t border-gray-200 pt-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Share2 className="h-5 w-5 text-orange-600" />
                    Referral Details
                  </h3>

                  {selectedCollege.referredBy ? (
                    <div className="grid grid-cols-1 gap-4">
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 p-4 border border-gray-200 rounded-lg bg-gray-50">
                        <div className="space-y-2">
                          <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                            <Label className="text-sm font-medium text-gray-500 min-w-24">Name:</Label>
                            <span className="text-gray-900 font-medium break-words">
                              {selectedCollege.referredBy.username || 'Not provided'}
                            </span>
                          </div>
                          <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                            <Label className="text-sm font-medium text-gray-500 min-w-24">Email:</Label>
                            <span className="text-gray-900 break-all">
                              {selectedCollege.referredBy.email || 'Not provided'}
                            </span>
                          </div>
                        </div>
                        <div className="space-y-2">
                          <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                            <Label className="text-sm font-medium text-gray-500 min-w-24">Phone:</Label>
                            <span className="text-gray-900 break-words">
                              {selectedCollege.referredBy.phone || 'Not provided'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
                      <p className="text-gray-500 text-center">No referral details available for this college.</p>
                    </div>
                  )}
                </div>

                {/* Led By Details */}
                {selectedCollege.referredBy?.ledby ? (
                  <div className="space-y-4">
                    <div className="border-t border-gray-200 pt-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        <UsersIcon className="h-5 w-5 text-orange-600" />
                        Led By Details
                      </h3>
                      
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 p-4 border border-gray-200 rounded-lg bg-gray-50">
                        <div className="space-y-2">
                          <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                            <Label className="text-sm font-medium text-gray-500 min-w-20">Name:</Label>
                            <span className="text-gray-900 font-medium break-words">
                              {typeof selectedCollege.referredBy.ledby === 'object' 
                                ? selectedCollege.referredBy.ledby.username || 'Not provided'
                                : selectedCollege.referredBy.ledby || 'Not provided'}
                            </span>
                          </div>
                          <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                            <Label className="text-sm font-medium text-gray-500 min-w-20">Phone:</Label>
                            <span className="text-gray-900 break-words">
                              {typeof selectedCollege.referredBy.ledby === 'object' 
                                ? selectedCollege.referredBy.ledby.phone || 'Not provided'
                                : 'Not provided'}
                            </span>
                          </div>
                        </div>
                        <div className="space-y-2">
                          <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                            <Label className="text-sm font-medium text-gray-500 min-w-20">Email:</Label>
                            <span className="text-gray-900 break-all">
                              {typeof selectedCollege.referredBy.ledby === 'object' 
                                ? selectedCollege.referredBy.ledby.email || 'Not provided'
                                : 'Not provided'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="border-t border-gray-200 pt-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        <UsersIcon className="h-5 w-5 text-orange-600" />
                        Led By Details
                      </h3>
                      <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
                        <p className="text-gray-500 text-center">No Led By details available for this college.</p>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* SPOC Details */}
                {selectedCollege.spocDetails && selectedCollege.spocDetails.length > 0 ? (
                  <div className="space-y-4">
                    <div className="border-t border-gray-200 pt-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        <UsersIcon className="h-5 w-5 text-orange-600" />
                        SPOC Details ({selectedCollege.spocDetails.length})
                      </h3>
                      
                      <div className="max-h-60 overflow-y-auto space-y-3 pr-2">
                        {selectedCollege.spocDetails.map((spoc, index) => (
                        <div key={index} className="grid grid-cols-1 lg:grid-cols-2 gap-4 p-4 border border-gray-200 rounded-lg bg-gray-50">
                          <div className="space-y-2">
                            <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                              <Label className="text-sm font-medium text-gray-500 min-w-20">Name:</Label>
                              <span className="text-gray-900 font-medium break-words">{spoc.name}</span>
                            </div>
                            <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                              <Label className="text-sm font-medium text-gray-500 min-w-20">Phone:</Label>
                              <span className="text-gray-900 break-words">{spoc.phone}</span>
                            </div>
                          </div>
                          <div className="space-y-2">
                            <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                              <Label className="text-sm font-medium text-gray-500 min-w-20">Email:</Label>
                              <span className="text-gray-900 break-all">{spoc.email || 'Not provided'}</span>
                            </div>
                            <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                              <Label className="text-sm font-medium text-gray-500 min-w-20">designation:</Label>
                              <span className="text-gray-900 break-words">{spoc.designation}</span>
                            </div>
                          </div>
                        </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="border-t border-gray-200 pt-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        <UsersIcon className="h-5 w-5 text-orange-600" />
                        SPOC Details
                      </h3>
                      <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
                        <p className="text-gray-500 text-center">No SPOC details available for this college.</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Status Change History */}
                <div className="border-t border-gray-200 pt-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <History className="h-5 w-5 text-orange-600" />
                    Status Change History
                  </h3>

                  {selectedCollege.statusChangeHistory && selectedCollege.statusChangeHistory.length > 0 ? (
                    <div className="space-y-3">
                      {selectedCollege.statusChangeHistory.map((history) => (
                        <div key={history._id} className="p-4 border border-gray-200 rounded-lg bg-white shadow-sm">
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                              <Label className="text-sm font-medium text-gray-500">New Status</Label>
                              <p className="text-gray-900 font-semibold">{getStatusInfo(history.newStatus).label}</p>
                            </div>
                            {history.oldStatus && (
                              <div>
                                <Label className="text-sm font-medium text-gray-500">Previous Status</Label>
                                <p className="text-gray-900">{getStatusInfo(history.oldStatus).label}</p>
                              </div>
                            )}
                            <div>
                              <Label className="text-sm font-medium text-gray-500">Updated By</Label>
                              <p className="text-gray-900">{history.updatedBy || 'Unknown'}</p>
                            </div>
                          </div>
                          <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <Label className="text-sm font-medium text-gray-500">Updated At</Label>
                              <p className="text-gray-900">
                                {new Date(history.updatedAt).toLocaleDateString('en-US', {
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </p>
                            </div>
                            {history.remarks && (
                              <div>
                                <Label className="text-sm font-medium text-gray-500">Remarks</Label>
                                <p className="text-gray-900 break-words">{history.remarks}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
                      <p className="text-gray-500 text-center">No status history available for this college.</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter className="border-t pt-4 mt-4 justify-end">
            <Button 
              variant="outline" 
              onClick={() => setShowDetailsModal(false)}
              className="w-full sm:w-auto"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Status Change Modal */}
      <Dialog open={showStatusModal} onOpenChange={setShowStatusModal}>
        <DialogContent className="w-[95vw] max-w-2xl mx-auto">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold">Change College Status</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Current Status Display */}
            <div className="space-y-2">
              <Label className="text-sm font-medium text-gray-500">Current Status</Label>
              <Badge
                variant={getStatusInfo(statusChangeData.currentStatus).variant}
                className={getStatusInfo(statusChangeData.currentStatus).className}
              >
                {getStatusInfo(statusChangeData.currentStatus).label}
              </Badge>
            </div>

            {/* New Status Selection */}
            <div className="space-y-2">
              <Label htmlFor="newStatus" className="text-sm font-medium">
                New Status <span className="text-red-500">*</span>
              </Label>
              <Select
                value={statusChangeData.newStatus}
                onValueChange={(value) => setStatusChangeData(prev => ({ ...prev, newStatus: value as 'underProcess' | 'onboarded' | 'notOnboarded' }))}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select new status" />
                </SelectTrigger>
                <SelectContent>
       
                  <SelectItem value="added">Added</SelectItem>
                  <SelectItem value="approched">Approched</SelectItem>
                  <SelectItem value="inTalk">In Talk</SelectItem>
                  <SelectItem value="confirmed">Confirmed</SelectItem>
                  <SelectItem value="activated">Activated</SelectItem>
                  <SelectItem value="onHold">On Hold</SelectItem>
                  <SelectItem value="underProcess">Under Process</SelectItem>
                  <SelectItem value="onboarded">Onboarded</SelectItem>
                  <SelectItem value="notOnboarded">Not Onboarded</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Remarks Field */}
            <div className="space-y-2">
              <Label htmlFor="remarks" className="text-sm font-medium">
                Remarks {statusChangeData.newStatus === 'notOnboarded' && <span className="text-red-500">*</span>}
              </Label>
              <Textarea
                id="remarks"
                value={statusChangeData.remarks}
                onChange={(e) => setStatusChangeData(prev => ({ ...prev, remarks: e.target.value }))}
                className={`w-full min-h-[100px] ${statusChangeErrors.remarks ? "border-red-500 focus:border-red-500" : ""}`}
                placeholder={statusChangeData.newStatus === 'notOnboarded' ? "Please provide remarks for why the college is not onboarded..." : "Optional remarks about the status change..."}
              />
              {statusChangeErrors.remarks && (
                <p className="text-sm text-red-500">{statusChangeErrors.remarks}</p>
              )}
            </div>
          </div>
          
          <DialogFooter className="flex flex-col sm:flex-row gap-2 pt-4">
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => setShowStatusModal(false)}
              className="w-full sm:w-auto order-2 sm:order-1"
            >
              Cancel
            </Button>
            <Button 
              type="button" 
              onClick={handleStatusChange}
              disabled={loading} 
              className="w-full sm:w-auto bg-orange-600 hover:bg-orange-700 order-1 sm:order-2"
            >
              {loading ? 'Updating...' : 'Update Status'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CollegeReferralProgram;
